/**
 * Multi-Brand CMS Configuration
 *
 * Centralizes brand detection and Contentful configuration
 * for Gallet, Breitling, and Universal Genève.
 */

export type { Brand } from './brandConstants';
export { BRAND_ENUM } from './brandConstants';
export { requireBrand } from './requireBrand';
// Compatibility export. New shared code should import from `@/lib/brands/traits`.
// Keep this import relative: next.config.ts loads this module before application bundling.
export { BRAND_TRAITS, getBrandTraits, type BrandTraits } from '../brands/traits';

import type { Brand } from './brandConstants';
import { BRAND_ENUM } from './brandConstants';
import { requireBrand } from './requireBrand';

/**
 * Active brand for this build — `NEXT_PUBLIC_BRAND` must be set explicitly.
 */
export const BRAND: Brand = requireBrand('application runtime');

/**
 * True when the active brand uses Gallet's CMS shape. UG reuses Gallet's
 * Contentful schema/content model, so brand-divergent paths that key off "the
 * Gallet-shaped content model" should check this instead of repeating
 * `BRAND === BRAND_ENUM.Gallet || BRAND === BRAND_ENUM.Ug`.
 */
export const IS_GALLET_SHAPED: boolean = BRAND === BRAND_ENUM.Gallet || BRAND === BRAND_ENUM.Ug;

/**
 * Contentful configuration per brand
 */
export interface ContentfulBrandConfig {
  spaceId: string;
  environment: string;
  accessToken: string;
  previewToken: string;
}

/**
 * Brand-specific Contentful configurations
 */
export const CONTENTFUL_CONFIG: Record<Brand, ContentfulBrandConfig> = {
  gallet: {
    spaceId: process.env.GALLET_CONTENTFUL_SPACE_ID || '',
    environment: process.env.GALLET_CONTENTFUL_ENVIRONMENT || '',
    accessToken: process.env.GALLET_CONTENTFUL_ACCESS_TOKEN || '',
    previewToken: process.env.GALLET_CONTENTFUL_PREVIEW_TOKEN || '',
  },
  breitling: {
    spaceId: process.env.BREITLING_CONTENTFUL_SPACE_ID || '',
    environment: process.env.BREITLING_CONTENTFUL_ENVIRONMENT || '',
    accessToken: process.env.BREITLING_CONTENTFUL_ACCESS_TOKEN || '',
    previewToken: process.env.BREITLING_CONTENTFUL_PREVIEW_TOKEN || '',
  },
  ug: {
    spaceId: process.env.UG_CONTENTFUL_SPACE_ID || '',
    environment: process.env.UG_CONTENTFUL_ENVIRONMENT || '',
    accessToken: process.env.UG_CONTENTFUL_ACCESS_TOKEN || '',
    previewToken: process.env.UG_CONTENTFUL_PREVIEW_TOKEN || '',
  },
} as const;

/**
 * Get Contentful configuration for a specific brand
 * @param brand - Brand to get configuration for (defaults to current BRAND)
 */
export function getContentfulConfig(brand: Brand = BRAND): ContentfulBrandConfig {
  return CONTENTFUL_CONFIG[brand];
}

/**
 * Check if a brand is configured (has required credentials)
 * @param brand - Brand to check
 */
export function isBrandConfigured(brand: Brand): boolean {
  const config = CONTENTFUL_CONFIG[brand];
  return !!(config.spaceId && config.accessToken);
}

/**
 * Get Contentful GraphQL endpoint for a brand
 * @param brand - Brand to get endpoint for (defaults to current BRAND)
 */
export function getContentfulEndpoint(brand: Brand = BRAND): string {
  const config = getContentfulConfig(brand);
  return `https://graphql.contentful.com/content/v1/spaces/${config.spaceId}/environments/${config.environment}`;
}

/**
 * Get authorization header for Contentful requests
 * @param brand - Brand to get auth for (defaults to current BRAND)
 * @param preview - Whether to use preview token
 */
export function getContentfulAuthHeader(brand: Brand = BRAND, preview = false): string {
  const config = getContentfulConfig(brand);
  const token = preview ? config.previewToken : config.accessToken;
  return `Bearer ${token}`;
}

const BRAND_DISPLAY_NAMES: Record<Brand, string> = {
  gallet: 'Gallet',
  breitling: 'Breitling',
  ug: 'Universal Genève',
};

export function getBrandDisplayName(brand: Brand = BRAND): string {
  return BRAND_DISPLAY_NAMES[brand];
}
